import React, { useState, useMemo, useCallback } from "react";
import {
    Search,
    Database,
    // LayoutTemplate,
    File,
    ShieldCheck,
    ClipboardCheck,
    // Image,
    TableOfContents,
    EthernetPort,
    Router,
    Shapes,
    // Shapes,
} from "lucide-react";
import { ConnectorCard, ConnectorItem } from "@/components/cards/ConnectorCard";
import { CategoryGroup } from "@/components/tabs/CategoryGroup";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useConnectorsQuery } from "@/api/queries/admin/ConnectorQueries";
import Loading from "@/components/loading/Loading";
import { AgentTypeKey } from "@shared/config/agent-registry";

// Map connector references to their respective icons
const iconMap: Record<AgentTypeKey, React.ReactNode> = {
    // dalle: <Image size={20} />,
    jira: <ClipboardCheck size={20} />,
    // api: <EthernetPort size={20} />,
    virustotal: <ShieldCheck size={20} />,
    web: <EthernetPort size={20} />,
    confluence: <TableOfContents size={20} />,
    sql: <Database size={20} />,
    // notion: <File size={20} />,
    // websites: <LayoutTemplate size={20} />,
    // files: <File size={20} />,
    router: <Router size={20} />,
    mediawiki: <File size={20} />,
    phpipam: <File size={20} />,
    synthesizer: <Shapes size={20} />,
    api: <EthernetPort size={20} />,
};

const getConnectorIcon = (key: string) => iconMap[key] || <Database size={20} />;

const Connectors: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const { data: connectors, isLoading } = useConnectorsQuery();

    // Memoize enhanced connectors with icons
    const items = useMemo(() => {
        if (!connectors) return [];
        return connectors.map((connector) => ({
            ...connector,
            icon: getConnectorIcon(connector.key),
        }));
    }, [connectors]);

    // Memoize filtered items based on search, tab, and status
    const filteredItems = useMemo(() => {
        return items.filter((item) => {
            const q = searchQuery.toLowerCase();
            const matchesSearch =
                item.name.toLowerCase().includes(q) ||
                item.description.toLowerCase().includes(q) ||
                item.category.toLowerCase().includes(q);

            let matchesType = true;
            switch (activeTab) {
                case "essential":
                    matchesType = item.category === "Essential";
                    break;
                case "knowledgebase":
                    matchesType = item.category === "Knowledgebase";
                    break;
                case "image":
                    matchesType = item.category === "Image";
                    break;
                case "web":
                    matchesType = item.category === "Web";
                    break;
                case "misc":
                    matchesType = item.category === "Misc";
                    break;
                case "new":
                    matchesType = item.ribbon === "New";
                    break;
                case "updated":
                    matchesType = item.ribbon === "Updated";
                    break;
            }

            const matchesStatus = statusFilter === "all" || item.status === statusFilter;
            return matchesSearch && matchesType && matchesStatus;
        });
    }, [items, searchQuery, activeTab, statusFilter]);

    // Memoize grouped items by category
    const groupedItems = useMemo<Record<string, ConnectorItem[]>>(() => {
        return filteredItems.reduce(
            (acc, item: any) => {
                if (!acc[item.category]) acc[item.category] = [];
                acc[item.category].push(item);
                return acc;
            },
            {} as Record<string, ConnectorItem[]>
        );
    }, [filteredItems]);

    const resetFilters = useCallback(() => {
        setSearchQuery("");
        setStatusFilter("all");
        setActiveTab("all");
    }, []);

    if (isLoading) {
        return (
            <div className="absolute inset-0">
                <Loading />
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col">
            {/* Top Header with Search */}
            <header className="border-b border-border bg-background p-5">
                <div className="mb-5 flex items-center justify-between">
                    <h1 className="text-xl font-bold">Configure Connectors</h1>
                </div>

                {/* Search and filter controls */}
                <div className="flex flex-wrap gap-4">
                    <div className="relative max-w-md flex-grow">
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 transform text-muted-foreground"
                            size={18}
                        />
                        <Input
                            type="text"
                            placeholder="Search connectors..."
                            className="pl-10"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="Active">Active</SelectItem>
                            <SelectItem value="Inactive">Inactive</SelectItem>
                            <SelectItem value="Not used">Not used</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </header>

            {/* Tab Navigation */}
            <div className="border-b border-border bg-background px-5 pt-2">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="h-auto justify-start space-x-6 bg-transparent p-0">
                        {["all", "knowledgebase", "image", "web", "misc", "new", "updated"].map((tab) => (
                            <TabsTrigger
                                key={tab}
                                value={tab}
                                className="relative rounded-none border-b-2 border-transparent px-1 py-4 text-sm font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            </div>

            {/* Content Area */}
            <div className="flex flex-1 flex-col gap-10 overflow-y-auto overflow-x-hidden bg-background p-6">
                {Object.keys(groupedItems).length > 0 ? (
                    Object.entries(groupedItems).map(([category, items]) => (
                        <CategoryGroup key={category} category={category}>
                            {items.map((item) => (
                                <ConnectorCard key={item.key} item={item} className="h-full" />
                            ))}
                        </CategoryGroup>
                    ))
                ) : (
                    <div className="my-12 flex h-64 flex-col items-center justify-center">
                        <div className="mb-5 rounded-full bg-accent p-5">
                            <Search size={30} className="text-muted-foreground" />
                        </div>
                        <h3 className="mb-2 text-xl font-medium">No matches found</h3>
                        <p className="max-w-md text-center text-muted-foreground">
                            Try adjusting your search criteria or filters to find what you're looking for
                        </p>
                        <Button onClick={resetFilters} className="mt-5">
                            Reset filters
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Connectors;
